import { SingleSelectDropdown, Text } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState, type ReactNode } from "react";
import type { TrainingSessionListView } from "@/api";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import type { LoadState } from "@/components/dataset";
import { createLogger } from "@/logging";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { loadConfigDocument, type TrainingSessionConfigDocument } from "../sessionConfigDocument";
import { CodeDiffViewer } from "@/components/codeEditor";
import { sessionDisplayName } from "../trainingSessionFormat";
import type { SessionCompareController } from "./useSessionCompareController";
import styles from "./SessionCompareConfigSection.module.css";

const logger = createLogger("page/training/compare");

type SessionCompareConfigSectionProps = {
  sessions: readonly TrainingSessionListView[];
  controller: SessionCompareController;
};

// 한쪽(기준/대상)의 config 문서 준비 상태. placeholder 문구를 단계마다 구분한다.
type ConfigSideState =
  | { kind: "detailLoading" }
  | { kind: "detailError" }
  | { kind: "noRun" }
  | { kind: "docLoading" }
  | { kind: "docError"; message: string }
  | { kind: "noDoc" }
  | { kind: "ready"; document: TrainingSessionConfigDocument };

// Config 비교 카드. 기준(base)과 대상 세션을 드롭다운으로 고른다. 두 세션의 config 전문(MLflow
// 아티팩트)을 새 학습 위저드와 같은 Monaco inline diff 로 겹쳐 표시한다. 문서는 run 단위로
// 캐시한다. 그래서 기준과 대상을 바꿔도 다시 조회하지 않는다.
export function SessionCompareConfigSection({
  sessions,
  controller,
}: SessionCompareConfigSectionProps): ReactNode {
  const [baseId, setBaseId] = useState<string | null>(sessions[0]?.id ?? null);
  const [targetId, setTargetId] = useState<string | null>(sessions[1]?.id ?? null);
  const [docStates, setDocStates] = useState<
    Record<string, LoadState<TrainingSessionConfigDocument | null>>
  >({});
  const [retryTick, setRetryTick] = useState(0);
  // run 단위 in-flight/결과 캐시. 미제공(null)과 실패는 지운다. 그래서 다시 고르면 다시 시도한다.
  const requestsRef = useRef(new Map<string, Promise<TrainingSessionConfigDocument | null>>());

  const options = sessions.map((session) => ({
    value: session.id,
    label: sessionDisplayName(session),
  }));

  // 같은 세션을 양쪽에 두지 않고 서로 바꾼다. 두 드롭다운은 항상 서로 다른 세션을 가리킨다.
  const handleBaseChange = (next: string) => {
    if (next === targetId) {
      setTargetId(baseId);
    }
    setBaseId(next);
  };
  const handleTargetChange = (next: string) => {
    if (next === baseId) {
      setBaseId(targetId);
    }
    setTargetId(next);
  };

  const baseRunId = runIdOf(controller, baseId);
  const targetRunId = runIdOf(controller, targetId);

  useEffect(() => {
    const runIds = [...new Set([baseRunId, targetRunId])].filter(
      (runId): runId is string => typeof runId === "string",
    );
    if (runIds.length === 0) {
      return;
    }
    let cancelled = false;
    for (const runId of runIds) {
      setDocStates((prev) =>
        prev[runId]?.status === "success" ? prev : { ...prev, [runId]: { status: "loading" } },
      );
      let request = requestsRef.current.get(runId);
      if (!request) {
        request = loadConfigDocument(runId);
        requestsRef.current.set(runId, request);
      }
      void request
        .then((document) => {
          if (document === null) {
            requestsRef.current.delete(runId);
          }
          if (!cancelled) {
            setDocStates((prev) => ({ ...prev, [runId]: { status: "success", data: document } }));
          }
        })
        .catch((error: unknown) => {
          requestsRef.current.delete(runId);
          if (!cancelled) {
            logger.error(`config artifact lookup failed: ${runId}`, error);
            setDocStates((prev) => ({
              ...prev,
              [runId]: { status: "error", message: "Config 전문을 불러오지 못했습니다." },
            }));
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [baseRunId, targetRunId, retryTick]);

  const baseSide = resolveSideState(controller, docStates, baseId);
  const targetSide = resolveSideState(controller, docStates, targetId);
  const bothReady = baseSide.kind === "ready" && targetSide.kind === "ready";
  // 문서 조회는 run 단위로 다시 받는다. 그래서 어느 줄의 버튼을 눌러도 실패한 문서를 모두 다시 받는다.
  const retryDocuments = () => setRetryTick((tick) => tick + 1);

  return (
    <section className={styles.card} aria-label="Config 비교">
      <div className={styles.cardHeader}>
        <Text variant={hatcheryTextModes["caption-bold"]}>Config 비교</Text>
      </div>
      <div className={styles.controls}>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="기준(base)"
          value={baseId}
          options={options}
          onValueChange={handleBaseChange}
        />
        <span className={styles.arrow} aria-hidden>
          →
        </span>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label="대상"
          value={targetId}
          options={options}
          onValueChange={handleTargetChange}
        />
      </div>
      {bothReady ? (
        <>
          {baseSide.document.body === targetSide.document.body ? (
            <p className={styles.muted}>두 Session의 config 전문이 동일합니다.</p>
          ) : null}
          <div className={styles.diffShell}>
            <CodeDiffViewer
              original={baseSide.document.body}
              modified={targetSide.document.body}
              language="yaml"
            />
          </div>
          <p className={styles.sourceMeta}>
            기준 {baseSide.document.source} · 대상 {targetSide.document.source}
          </p>
        </>
      ) : (
        <div className={styles.sideNotes}>
          <ConfigSideNote label="기준" side={baseSide} onRetry={retryDocuments} />
          <ConfigSideNote label="대상" side={targetSide} onRetry={retryDocuments} />
        </div>
      )}
    </section>
  );
}

// 선택 세션의 run id. undefined 는 상세 미도착(로딩·실패), null 은 run 없음(대기 세션 등)이다.
function runIdOf(
  controller: SessionCompareController,
  sessionId: string | null,
): string | null | undefined {
  if (sessionId === null) {
    return undefined;
  }
  const detail = controller.loads[sessionId]?.detail;
  return detail?.status === "success" ? (detail.data.mlflow_run_id ?? null) : undefined;
}

function resolveSideState(
  controller: SessionCompareController,
  docStates: Readonly<Record<string, LoadState<TrainingSessionConfigDocument | null>>>,
  sessionId: string | null,
): ConfigSideState {
  const detail = sessionId === null ? undefined : controller.loads[sessionId]?.detail;
  if (detail?.status === "error") {
    return { kind: "detailError" };
  }
  if (detail?.status !== "success") {
    return { kind: "detailLoading" };
  }
  const runId = detail.data.mlflow_run_id ?? null;
  if (runId === null) {
    return { kind: "noRun" };
  }
  const doc = docStates[runId];
  if (doc === undefined || doc.status === "idle" || doc.status === "loading") {
    return { kind: "docLoading" };
  }
  if (doc.status === "error") {
    return { kind: "docError", message: doc.message };
  }
  if (doc.data === null) {
    return { kind: "noDoc" };
  }
  return { kind: "ready", document: doc.data };
}

const SIDE_NOTE_TEXT: Record<Exclude<ConfigSideState["kind"], "ready" | "docError">, string> = {
  detailLoading: "Session 상세를 불러오는 중입니다.",
  detailError: "Session 상세 로드에 실패해 config 를 확인할 수 없습니다.",
  noRun: "실행 기록(run)이 아직 없어 config 문서가 없습니다.",
  docLoading: "Config 문서를 불러오는 중입니다.",
  noDoc: "Config 문서를 찾지 못했습니다(MLflow Artifact 없음).",
};

function ConfigSideNote({
  label,
  side,
  onRetry,
}: {
  label: string;
  side: ConfigSideState;
  onRetry: () => void;
}): ReactNode {
  if (side.kind === "ready") {
    return null;
  }
  const message = side.kind === "docError" ? side.message : SIDE_NOTE_TEXT[side.kind];
  if (side.kind === "detailLoading" || side.kind === "docLoading") {
    return (
      <LoadingState size="sm">
        {label} — {message}
      </LoadingState>
    );
  }
  return (
    <EmptyState
      size="sm"
      tone={side.kind === "docError" || side.kind === "detailError" ? "error" : "neutral"}
      action={side.kind === "docError" ? { label: "다시 시도", onClick: onRetry } : undefined}
    >
      {label} — {message}
    </EmptyState>
  );
}
